
import { Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogContentText, Fab, Paper, TableContainer } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { useCallback, useState } from "react";
import { selectSettings } from "../settings/settingsSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Localized, useLocalization } from "@fluent/react";
import { useNavigate } from "react-router-dom";
import { MeasurementHeader } from "./measurementsApi";
import { FlexDiv } from "../../app/Utils";
import AddIcon from '@mui/icons-material/Add'
import { selectAllMeasurementHeaders, removeMeasurementHeader } from './measurementHeadersSlice'
import { CreateMeasurement } from './CreateMeasurement'
import { QuickSearchToolbar } from '../../app/QuickSearchToolbar'
import { AppId } from '../../app/appTypes'

export const MeasurementsList = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const rows = useAppSelector(selectAllMeasurementHeaders)
    const [itemToDelete, setItemToDelete] = useState<AppId | undefined>(undefined)
    const [dialogOpen, setDialogOpen] = useState(false)

    const getCustomLocaleText = () => {
        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        }
        return enUS.components.MuiDataGrid.defaultProps.localeText;
    };

    const handleGoToClick = useCallback((id: AppId) => {
        navigate(`/measurements/edit/${id}`)
    }, [navigate]);

    const handleAddClick = useCallback(() => {
        setDialogOpen(true)
    }, [])

    const handleDeleteClick = useCallback((id: AppId) => {
        setItemToDelete(id)
    }, [])

    const handleCancelDelete = useCallback(() => {
        setItemToDelete(undefined)
    }, [])

    const handleConfirmDelete = useCallback(() => {
        if (itemToDelete) {
            dispatch(removeMeasurementHeader(itemToDelete))
            setItemToDelete(undefined)
        }
    }, [itemToDelete, dispatch])

    const columns: GridColDef<MeasurementHeader>[] = [
        {
            field: 'name',
            headerName: l10n.getString('measurement-name', null, 'Nazwa pomiaru'),
            flex: 1,
        },
        {
            field: 'clearRoomId',
            headerName: l10n.getString('measurement-clean-room-id', null, 'Pomieszczenie czyste'),
            width: 200,
        },
        {
            field: 'measurements',
            headerName: l10n.getString('measurement-types', null, 'Types'),
            width: 250,
            renderCell: (params) => {
                const types = Object.keys(params.row.measurements ?? {})
                return <span>{types.join(', ')}</span>
            },
        },
        {
            field: 'actions',
            type: 'actions',
            width: 200,
            renderCell: (params) => (
                <ButtonGroup>
                    <Button onClick={() => handleGoToClick(params.row.id)} color="secondary" variant="outlined">
                        <Localized id="edit"><span>Edytuj</span></Localized>
                    </Button>
                    <Button color="primary" onClick={() => handleDeleteClick(params.row.id)}>
                        <Localized id="delete"><span>Usuń</span></Localized>
                    </Button>
                </ButtonGroup>
            ),
        },
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <div style={{ display: 'flex', height: '100%' }}>
                <div style={{ flexGrow: 1 }}>
                    <TableContainer component={Paper}>
                        <DataGrid
                            localeText={getCustomLocaleText()}
                            rows={rows}
                            columns={columns}
                            style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                            slots={{ toolbar: QuickSearchToolbar }}
                            onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                        />
                    </TableContainer>
                </div>
            </div>
            <FlexDiv>
                <Fab color="secondary" sx={{ marginTop: 2 }} onClick={handleAddClick}>
                    <AddIcon />
                </Fab>
            </FlexDiv>
            <CreateMeasurement open={dialogOpen} onClose={() => setDialogOpen(false)} />
            <Dialog open={itemToDelete !== undefined}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="confirm-delete">
                            <span>Czy napewno chcesz usunąć?</span>
                        </Localized>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmDelete}>
                        <Localized id="yes"><span>Tak</span></Localized>
                    </Button>
                    <Button onClick={handleCancelDelete}>
                        <Localized id="no"><span>Nie</span></Localized>
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
